import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ScrollText, Calendar, BarChart3, Package, Printer } from 'lucide-react';
import api from '../services/api';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const ProjectRestockHistory = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [selectedRestock, setSelectedRestock] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [periode, setPeriode] = useState('tout');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [filters, setFilters] = useState({
    date_debut: '',
    date_fin: ''
  });
  const printRef = useRef();

  useEffect(() => {
    loadHistory();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, periode, filters.date_debut, filters.date_fin]);

  const loadSettings = async () => {
    try {
      const res = await api.get('/business/settings');
      setSettings(res.data);
    } catch (err) {
      logger.error('Erreur chargement paramètres:', err);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Calculer les dates selon la période
      const now = new Date();
      let dateDebut, dateFin;
      
      switch (periode) {
        case 'tout':
          dateDebut = '2000-01-01';
          dateFin = now.toISOString().split('T')[0];
          break;
        case 'aujourdhui':
          dateDebut = now.toISOString().split('T')[0];
          dateFin = now.toISOString().split('T')[0];
          break;
        case 'semaine':
          const weekStart = new Date(now);
          const day = weekStart.getDay();
          const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
          weekStart.setDate(diff);
          dateDebut = weekStart.toISOString().split('T')[0];
          dateFin = now.toISOString().split('T')[0];
          break;
        case 'mois':
          dateDebut = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          dateFin = now.toISOString().split('T')[0];
          break;
        case 'annee':
          dateDebut = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
          dateFin = now.toISOString().split('T')[0];
          break;
        case 'custom':
          dateDebut = filters.date_debut || new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          dateFin = filters.date_fin || now.toISOString().split('T')[0];
          break;
        default:
          dateDebut = '2000-01-01';
          dateFin = now.toISOString().split('T')[0];
      }
      
      params.append('date_debut', dateDebut);
      params.append('date_fin', dateFin);
      
      const res = await api.get(`/projects/${projectId}/workspace/restock-history?${params.toString()}`);
      setHistory(res.data.history || []);
    } catch (err) {
      logger.error('Erreur chargement historique:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodeChange = (newPeriode) => {
    setPeriode(newPeriode);
    if (newPeriode === 'custom') {
      setShowCustomDates(true);
    } else {
      setShowCustomDates(false);
    }
  };

  const loadDetails = async (restockId) => {
    try {
      setDetailsLoading(true);
      const res = await api.get(`/projects/${projectId}/workspace/restock-history/${restockId}`);
      setSelectedRestock(res.data);
    } catch (err) {
      logger.error('Erreur chargement détails:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePrint = () => {
    if (!selectedRestock) return;
    
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Ravitaillement ${selectedRestock.reference}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-info { display: flex; gap: 15px; align-items: flex-start; }
            .company-logo { width: 70px; height: 70px; object-fit: contain; border-radius: 8px; }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .company-details { font-size: 12px; color: #6b7280; line-height: 1.6; }
            .doc-title { text-align: right; }
            .doc-title h1 { font-size: 22px; color: #10b981; margin-bottom: 5px; }
            .doc-title .ref { font-size: 14px; font-weight: bold; color: #374151; }
            .doc-title .date { font-size: 12px; color: #6b7280; margin-top: 5px; }
            .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-around; text-align: center; }
            .summary-item { }
            .summary-value { font-size: 18px; font-weight: bold; color: #10b981; }
            .summary-label { font-size: 11px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #10b981; color: white; padding: 10px 8px; text-align: left; font-size: 11px; text-transform: uppercase; }
            td { padding: 10px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
            tr:nth-child(even) { background: #f9fafb; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; text-align: center; }
            .total-row { font-weight: bold; background: #d1fae5 !important; }
          </style>
        </head>
        <body>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header avec sélecteur de période */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link to={`/projects/${projectId}/workspace/restock`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2"><ScrollText className="w-6 h-6" /> Historique des ravitaillements</h1>
            <p className="text-gray-500 text-sm">Consultez l'historique de vos ravitaillements de stock</p>
          </div>
        </div>
        
        {/* Boutons de période */}
        <div className="flex flex-wrap gap-1">
          {[
            { key: 'tout', label: 'Tout' },
            { key: 'aujourdhui', label: "Aujourd'hui" },
            { key: 'semaine', label: 'Semaine' },
            { key: 'mois', label: 'Mois' },
            { key: 'annee', label: 'Année' },
            { key: 'custom', label: <Calendar className="w-4 h-4" />, isIcon: true },
          ].map((p) => (
            <button
              key={p.key}
              onClick={() => handlePeriodeChange(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                periode === p.key 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sélecteur de dates personnalisées */}
      {showCustomDates && (
        <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Du:</label>
            <input
              type="date"
              value={filters.date_debut}
              onChange={(e) => setFilters({...filters, date_debut: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Au:</label>
            <input
              type="date"
              value={filters.date_fin}
              onChange={(e) => setFilters({...filters, date_fin: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          {filters.date_debut && filters.date_fin && (
            <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
              <BarChart3 className="w-4 h-4" /> Période: {new Date(filters.date_debut).toLocaleDateString('fr-FR')} - {new Date(filters.date_fin).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des ravitaillements */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="bg-emerald-600 text-white px-4 py-3">
              <h2 className="font-semibold">Ravitaillements ({history.length})</h2>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {history.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Package className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                  <p>Aucun ravitaillement trouvé</p>
                </div>
              ) : (
                history.map(item => (
                  <button
                    key={item.id}
                    onClick={() => loadDetails(item.id)}
                    className={`w-full p-4 text-left hover:bg-emerald-50 transition ${
                      selectedRestock?.id === item.id ? 'bg-emerald-100 border-l-4 border-emerald-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-semibold text-emerald-700">{item.reference}</span>
                      <span className="text-xs text-gray-500">{formatDate(item.date_ravitaillement)}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {item.total_produits} produits • {item.total_unites} unités
                    </div>
                    <div className="text-sm font-semibold text-gray-800">
                      {parseFloat(item.cout_total).toLocaleString()} XOF
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Détails du ravitaillement sélectionné */}
        <div className="lg:col-span-2">
          {detailsLoading ? (
            <div className="bg-white rounded-xl shadow p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-600"></div>
            </div>
          ) : selectedRestock ? (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="bg-emerald-600 text-white px-4 py-3 flex justify-between items-center">
                <h2 className="font-semibold">Détails - {selectedRestock.reference}</h2>
                <button
                  onClick={handlePrint}
                  className="px-3 py-1 bg-white/20 rounded-lg hover:bg-white/30 text-sm flex items-center gap-1"
                >
                  <Printer className="w-4 h-4" /> Imprimer
                </button>
              </div>
              
              {/* Résumé */}
              <div className="bg-emerald-50 p-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-700">{selectedRestock.total_produits}</div>
                  <div className="text-xs text-gray-600">Produits</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-700">{selectedRestock.total_unites}</div>
                  <div className="text-xs text-gray-600">Unités</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-700">{parseFloat(selectedRestock.cout_total).toLocaleString()}</div>
                  <div className="text-xs text-gray-600">Coût total (XOF)</div>
                </div>
              </div>

              {/* Informations */}
              <div className="p-4 border-b bg-gray-50">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Date:</span>{' '}
                    <span className="font-medium">{formatDate(selectedRestock.date_ravitaillement)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Référence:</span>{' '}
                    <span className="font-medium text-emerald-700">{selectedRestock.reference}</span>
                  </div>
                  {selectedRestock.expense_id && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Décaissement:</span>{' '}
                      <span className="font-medium text-green-600">✓ Enregistré (ID: {selectedRestock.expense_id})</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tableau des produits */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Produit</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock avant</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Qté ajoutée</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock après</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Prix unit.</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Coût</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedRestock.items?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{item.product_nom}</div>
                          {item.product_code && (
                            <div className="text-xs text-gray-500">{item.product_code}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{item.stock_avant}</td>
                        <td className="px-4 py-3 text-center font-semibold text-green-600">+{item.quantite}</td>
                        <td className="px-4 py-3 text-center font-semibold text-emerald-700">{item.stock_apres}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{parseFloat(item.prix_achat).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium">{parseFloat(item.cout_ligne).toLocaleString()} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-emerald-50">
                    <tr>
                      <td colSpan="5" className="px-4 py-3 text-right font-semibold">TOTAL</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">
                        {parseFloat(selectedRestock.cout_total).toLocaleString()} XOF
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedRestock.notes && (
                <div className="p-4 border-t bg-gray-50">
                  <div className="text-sm text-gray-500">Notes:</div>
                  <div className="text-gray-700">{selectedRestock.notes}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-12 text-center">
              <ArrowLeft className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Sélectionnez un ravitaillement</h3>
              <p className="text-gray-500">Cliquez sur un ravitaillement à gauche pour voir les détails</p>
            </div>
          )}
        </div>
      </div>

      {/* Contenu imprimable (caché) */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {selectedRestock && (
            <>
              {/* En-tête entreprise */}
              <div className="header">
                <div className="company-info">
                  {settings?.logo && (
                    <img 
                      src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                      alt="Logo"
                      className="company-logo"
                    />
                  )}
                  <div>
                    <div className="company-name">{settings?.nom_entreprise || 'Mon Entreprise'}</div>
                    <div className="company-details">
                      {settings?.adresse && <div>{settings.adresse}</div>}
                      {settings?.telephone && <div>Tél: {settings.telephone}</div>}
                      {settings?.email && <div>Email: {settings.email}</div>}
                      {settings?.numero_registre && <div>RCCM: {settings.numero_registre}</div>}
                      {settings?.numero_fiscal && <div>NIF: {settings.numero_fiscal}</div>}
                    </div>
                  </div>
                </div>
                <div className="doc-title">
                  <h1>BON DE RAVITAILLEMENT</h1>
                  <div className="ref">{selectedRestock.reference}</div>
                  <div className="date">{formatDate(selectedRestock.date_ravitaillement)}</div>
                </div>
              </div>

              {/* Résumé */}
              <div className="summary">
                <div className="summary-item">
                  <div className="summary-value">{selectedRestock.total_produits}</div>
                  <div className="summary-label">Produits</div>
                </div>
                <div className="summary-item">
                  <div className="summary-value">{selectedRestock.total_unites}</div>
                  <div className="summary-label">Unités</div>
                </div>
                <div className="summary-item">
                  <div className="summary-value">{parseFloat(selectedRestock.cout_total).toLocaleString()} XOF</div>
                  <div className="summary-label">Coût total</div>
                </div>
              </div>

              {/* Tableau */}
              <table>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th className="text-center">Code</th>
                    <th className="text-center">Stock avant</th>
                    <th className="text-center">Qté ajoutée</th>
                    <th className="text-center">Stock après</th>
                    <th className="text-right">Prix unitaire</th>
                    <th className="text-right">Coût</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRestock.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.product_nom}</td>
                      <td className="text-center">{item.product_code || '-'}</td>
                      <td className="text-center">{item.stock_avant}</td>
                      <td className="text-center">+{item.quantite}</td>
                      <td className="text-center">{item.stock_apres}</td>
                      <td className="text-right">{parseFloat(item.prix_achat).toLocaleString()} XOF</td>
                      <td className="text-right">{parseFloat(item.cout_ligne).toLocaleString()} XOF</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="6" className="text-right">TOTAL</td>
                    <td className="text-right">{parseFloat(selectedRestock.cout_total).toLocaleString()} XOF</td>
                  </tr>
                </tbody>
              </table>

              {selectedRestock.notes && (
                <div style={{ marginTop: '20px', padding: '10px', background: '#f3f4f6', borderRadius: '8px' }}>
                  <strong>Notes:</strong> {selectedRestock.notes}
                </div>
              )}

              {/* Pied de page */}
              <div className="footer">
                <p>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
                {settings?.nom_entreprise && <p>© {new Date().getFullYear()} {settings.nom_entreprise}</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectRestockHistory;
